import React from 'react';
import { useDemo } from '../../state/DemoContext';
import './connectsystems.css';

const ConnectSystems = () => {
  const { state, actions } = useDemo();

  const handleConnect = (connectorId) => {
    actions.connectSystem(connectorId);
    actions.showToast(`${state.connectors.find(c => c.id === connectorId)?.name} connected successfully!`);
  };

  const handleDisconnect = (connectorId) => {
    actions.disconnectSystem(connectorId);
    actions.showToast(`${state.connectors.find(c => c.id === connectorId)?.name} disconnected`);
  };

  const requiredConnectors = state.connectors.filter(c => c.required);
  const connectedRequired = requiredConnectors.filter(c => c.status === 'connected').length;
  const allRequiredConnected = connectedRequired >= requiredConnectors.length;

  return (
    <div className="jo-xyz-demo-connect-systems-page">
      <div className="jo-xyz-demo-connect-systems-page__header">
        <h1>Connect Systems</h1>
        <p className="jo-xyz-demo-connect-systems-page__subtitle">
          Integrate your business systems to enable real-time asset monitoring and borrowing base calculations
        </p>
      </div>

      {/* Connection Status */}
      <div className="jo-xyz-demo-connect-systems-page__status">
        <div className="jo-xyz-demo-card">
          <div className="jo-xyz-demo-card__header">
            <h3 className="jo-xyz-demo-card__title">Integration Status</h3>
            <p className="jo-xyz-demo-card__subtitle">
              {allRequiredConnected
                ? 'All required systems connected - Borrowing base ready!'
                : `${connectedRequired} of ${requiredConnectors.length} required systems connected`
              }
            </p>
          </div>
          <div className="jo-xyz-demo-connection-progress">
            <div className="jo-xyz-demo-connection-progress__bar">
              <div
                className="jo-xyz-demo-connection-progress__fill"
                style={{ width: `${(connectedRequired / requiredConnectors.length) * 100}%` }}
              />
            </div>
            <div className="jo-xyz-demo-connection-progress__text">
              {connectedRequired}/{requiredConnectors.length} Required Systems Connected
            </div>
          </div>
        </div>
      </div>

      {/* Connector Grid */}
      <div className="jo-xyz-demo-connect-systems-page__connectors">
        <div className="jo-xyz-demo-grid jo-xyz-demo-grid--2">
          {state.connectors.map((connector) => (
            <div key={connector.id} className="jo-xyz-demo-connector-tile">
              <div className="jo-xyz-demo-card">
                <div className="jo-xyz-demo-connector-tile__header">
                  <div className="jo-xyz-demo-connector-tile__icon">
                    {connector.icon}
                  </div>
                  <div className="jo-xyz-demo-connector-tile__info">
                    <h3 className="jo-xyz-demo-connector-tile__name">{connector.name}</h3>
                    <p className="jo-xyz-demo-connector-tile__description">{connector.description}</p>
                  </div>
                  <div className={`jo-xyz-demo-connector-tile__status jo-xyz-demo-connector-tile__status--${connector.status}`}>
                    {connector.status === 'connected' ? 'Connected' : 'Disconnected'}
                  </div>
                </div>

                <div className="jo-xyz-demo-connector-tile__content">
                  {connector.status === 'connected' ? (
                    <div className="jo-xyz-demo-connector-tile__connected">
                      <div className="jo-xyz-demo-connector-tile__stats">
                        <div className="jo-xyz-demo-stat">
                          <span className="jo-xyz-demo-stat__label">Records Synced</span>
                          <span className="jo-xyz-demo-stat__value">{connector.records.toLocaleString()}</span>
                        </div>
                        <div className="jo-xyz-demo-stat">
                          <span className="jo-xyz-demo-stat__label">Last Sync</span>
                          <span className="jo-xyz-demo-stat__value">
                            {connector.lastSync ? new Date(connector.lastSync).toLocaleTimeString() : 'Never'}
                          </span>
                        </div>
                      </div>
                      <button
                        className="jo-xyz-demo-btn jo-xyz-demo-btn--danger"
                        onClick={() => handleDisconnect(connector.id)}
                      >
                        Disconnect
                      </button>
                    </div>
                  ) : (
                    <div className="jo-xyz-demo-connector-tile__disconnected">
                      <div className="jo-xyz-demo-connector-tile__requirements">
                        <h4>Connection Requirements:</h4>
                        <ul>
                          <li>Valid API credentials</li>
                          <li>Network access to {connector.name}</li>
                          <li>Read permissions for financial data</li>
                        </ul>
                      </div>
                      <button
                        className="jo-xyz-demo-btn jo-xyz-demo-btn--primary"
                        onClick={() => handleConnect(connector.id)}
                      >
                        Connect {connector.name}
                      </button>
                    </div>
                  )}
                </div>

                {connector.required && (
                  <div className="jo-xyz-demo-connector-tile__required">
                    <span className="jo-xyz-demo-required-badge">Required</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Next Steps */}
      {allRequiredConnected && (
        <div className="jo-xyz-demo-connect-systems-page__next-steps">
          <div className="jo-xyz-demo-card">
            <div className="jo-xyz-demo-card__header">
              <h3 className="jo-xyz-demo-card__title">🎉 Integration Complete!</h3>
              <p className="jo-xyz-demo-card__subtitle">
                Your systems are now connected and your borrowing base is being calculated in real-time.
              </p>
            </div>
            <div className="jo-xyz-demo-next-steps">
              <div className="jo-xyz-demo-next-step">
                <div className="jo-xyz-demo-next-step__icon">✓</div>
                <div className="jo-xyz-demo-next-step__content">
                  <h4>Systems Connected</h4>
                  <p>All required systems are now integrated and syncing data</p>
                </div>
              </div>
              <div className="jo-xyz-demo-next-step">
                <div className="jo-xyz-demo-next-step__icon">📊</div>
                <div className="jo-xyz-demo-next-step__content">
                  <h4>Borrowing Base Ready</h4>
                  <p>Your borrowing capacity is now calculated and available</p>
                </div>
              </div>
              <div className="jo-xyz-demo-next-step">
                <div className="jo-xyz-demo-next-step__icon">🚀</div>
                <div className="jo-xyz-demo-next-step__content">
                  <h4>Ready for Funding</h4>
                  <p>You can now view offers and apply for funding</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectSystems;
